import { useMutation } from '@tanstack/react-query';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { TimeUnit } from '@mews/enums';
import { AvailabilityAdjustment, AvailabilityBlock } from '@mews-commander/data-access';
import { DateTimeService, DurationService } from '@mews/framework';
import { CommanderThunkAction } from '@mews-commander/types';
import { keyBy } from '@mews/fp';
import { createTimeUnitRangeArray, useCommanderThunkDispatch } from '@mews-commander/utils';
import { getServiceTimeUnit } from '@mews-commander/legacy-entities/src/ducks';
import {
    getCurrentEnterpriseOperationalEditableHistoryWindow,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
} from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getAvailabilityAdjustmentsByAvailabilityBlockId } from '@mews-commander/legacy-entities/src/ducks/availabilityAdjustments';
import { fetchAvailabilities } from '../../actions/availabilities';
import { setReleaseOverriden } from '../../actions/formActions';
import { FormValues } from './OverrideAvailabilityAdjustmentFields';
import { OverrideReleaseDialogData } from './types';

interface BlockReleaseParams {
    values: FormValues;
    availabilityBlock: AvailabilityBlock;
    options: OverrideReleaseDialogData;
}

const overrideBlockRelease =
    ({
        values,
        availabilityBlock: { id, startUtc, endUtc, serviceId },
        options,
    }: BlockReleaseParams): CommanderThunkAction<Promise<void>> =>
    async (dispatch, getState, { repositories }) => {
        const state = getState();
        const timeUnit = getServiceTimeUnit(state, { id: serviceId }) as TimeUnit;
        const timezone: string = getCurrentEnterpriseIanaTimeZoneIdentifier(state);

        const EHWseconds = DurationService.asSeconds(
            DurationService.parse(getCurrentEnterpriseOperationalEditableHistoryWindow(state))
        );

        const previousAdjustments: Record<string, AvailabilityAdjustment> = pipe(
            getAvailabilityAdjustmentsByAvailabilityBlockId(state, {
                availabilityBlockId: id,
            }),
            array.filter(
                (adjustment) =>
                    values.firstTimeUnitStartUtc <= adjustment.startUtc &&
                    adjustment.startUtc <= values.lastTimeUnitStartUtc &&
                    adjustment.resourceCategoryId === options.resourceCategoryId
            ),
            keyBy<AvailabilityAdjustment>('startUtc')
        );

        const dateTimeRange = createTimeUnitRangeArray({
            startDateTime: DateTimeService.fromUtcString(values.firstTimeUnitStartUtc, timezone),
            endDateTime: DateTimeService.fromUtcString(values.lastTimeUnitStartUtc, timezone),
            timeUnit,
            getRealHours: true,
        });

        await repositories.services.updateAvailability({
            serviceId,
            availabilityUpdates: dateTimeRange.map((dateTime) => {
                const utc = DateTimeService.toUtc(dateTime).value;

                return {
                    availabilityBlockId: { value: id },
                    resourceCategoryId: options.resourceCategoryId,
                    firstTimeUnitStartUtc: utc,
                    lastTimeUnitStartUtc: utc,
                    adjustmentValue: { value: previousAdjustments?.[utc]?.value ?? 0 },
                    releaseOverrideUtc: options.isReleaseOverride
                        ? DateTimeService.toUtc(DateTimeService.addSeconds(dateTime, EHWseconds)).value
                        : null,
                };
            }),
        });
        await dispatch(fetchAvailabilities({ startUtc, endUtc, serviceId }));
        if (options.isReleaseOverride) {
            dispatch(setReleaseOverriden());
        }
    };

export const useBlockReleaseOverrideMutation = () => {
    const dispatch = useCommanderThunkDispatch();

    return useMutation({
        mutationFn: async (params: BlockReleaseParams) => {
            await dispatch(overrideBlockRelease(params));
        },
    });
};
